import {
  CATEGORY_LABELS,
  SOURCE_TYPE_LABELS,
} from "@/components/risk-flagging/constants";
import type { CareHistoryDetail as Detail } from "@/lib/api/care";
import type { Escalation } from "@/lib/api/escalations";
import type { RiskFlag } from "@/lib/api/risk-flagging";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { REDACTED_LABEL } from "@/lib/redacted-display";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { PRIORITY_CONFIG } from "@/components/risk-flagging/constants";
import { cn } from "@/lib/utils";
import {
  ASSESSMENT_NAMES,
  CodeChip,
  Field,
  FieldLabel,
  Pill,
  ScoreTag,
} from "./care-primitives";
import { CareUpdateFieldsView } from "./care-update-section";

const CRISIS_SUBTYPE_LABELS: Record<string, string> = {
  SH: "Self-Harm",
  SI: "Suicidal Ideation",
  SA: "Suicide Attempt",
  HI: "Homicidal Ideation",
  Other: "Other",
};

const PRIORITY_PILL: Record<string, string> = {
  HIGH: "bg-[#f93b51] text-[#ffeaee]",
  MEDIUM: "border border-[#ff9812] text-[#ff9812]",
  STANDARD: "border border-[#aeaeae] text-[#3c3c3c]",
};

const DETAIL_LABEL = "text-[#a0998d]";
const DETAIL_VALUE = "text-sm leading-5 text-black";

export function CareHistoryDetail({ detail }: { detail: Detail }) {
  switch (detail.type) {
    case "assessment":
      return <AssessmentDetail payload={detail.payload} />;
    case "care_update":
      return (
        <Panel>
          <CareUpdateFieldsView symptomsFirst {...toView(detail.payload)} />
        </Panel>
      );
    case "presentation":
      return (
        <Panel>
          <div className="mb-4 flex items-center gap-2 border-b border-[#dfddd8] pb-4">
            <CodeChip code={detail.payload.code} size="sm" />
            <p className="font-heading text-base font-bold leading-6 text-[#1f1e1d]">
              {detail.payload.description}
            </p>
          </div>
          <CareUpdateFieldsView symptomsFirst {...toView(detail.payload)} />
        </Panel>
      );
    case "escalation":
      return <EscalationDetail escalation={detail.payload} />;
    case "flag":
      return (
        <div className="flex flex-col gap-3">
          {detail.payload.alerts.map((alert) => (
            <FlagDetail key={alert.id} alert={alert} />
          ))}
        </div>
      );
  }
}

function toView(payload: {
  clinical_summary: string | null;
  diagnostic_support_statement: string | null;
  assessment_of_symptoms: string | null;
}) {
  return {
    clinicalSummary: payload.clinical_summary,
    diagnosticSupportStatement: payload.diagnostic_support_statement,
    assessmentOfSymptoms: payload.assessment_of_symptoms,
  };
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[#cbc6be] bg-[rgba(246,244,239,0.5)] px-6 pb-8 pt-5">
      {children}
    </div>
  );
}

function AssessmentDetail({
  payload,
}: {
  payload: Extract<Detail, { type: "assessment" }>["payload"];
}) {
  return (
    <Panel>
      <div className="flex gap-10">
        <div className="flex flex-col gap-1">
          <FieldLabel className={DETAIL_LABEL}>Assigned assessment</FieldLabel>
          <p className="font-heading text-xl font-bold leading-6 text-[#1f1e1d]">
            {ASSESSMENT_NAMES[payload.assessment]}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel className={DETAIL_LABEL}>Score</FieldLabel>
          <ScoreTag
            score={payload.score}
            severity={payload.severity}
            max={payload.max_score}
            size="sm"
          />
        </div>
      </div>
      <table className="mt-4 w-full border-collapse font-body text-sm">
        <thead>
          <tr className="border-b border-[#dfddd8] text-left text-[#a0998d]">
            <th className="py-2 font-normal">Question</th>
            <th className="py-2 text-right font-normal">Answer</th>
          </tr>
        </thead>
        <tbody>
          {payload.answers.map((answer, index) => (
            <tr
              key={index}
              className="border-b border-[#ece8e1] last:border-b-0"
            >
              <td className="py-2 text-black">{answer.question}</td>
              <td className="py-2 text-right text-black">{answer.answer}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function EscalationDetail({ escalation }: { escalation: Escalation }) {
  const hidePhi = useDevOverridesStore((state) => state.hidePhiPii) === true;
  const meta = escalation.metadata ?? {};
  const mentee = escalation.mentee;
  const emergencyContact = [mentee?.guardian_name, mentee?.emergency_phone]
    .filter(Boolean)
    .join(" · ");
  const text = (key: string) => (meta[key] as string | undefined) || null;
  const shown = (value: string | null | undefined) =>
    hidePhi && value ? REDACTED_LABEL : value || "—";

  return (
    <Panel>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        <DetailField label="Escalation type">
          {meta.escalationType ? (
            <Pill
              tone={meta.escalationType === "Crisis" ? "crisis" : "outline"}
              className={meta.escalationType === "Crisis" ? "px-4" : undefined}
            >
              {meta.escalationType}
            </Pill>
          ) : (
            "—"
          )}
        </DetailField>
        <DetailField label="Crisis sub-type">
          {meta.crisisSubType ? (
            <Pill tone="outline">
              {CRISIS_SUBTYPE_LABELS[meta.crisisSubType] ?? meta.crisisSubType}
            </Pill>
          ) : (
            "—"
          )}
        </DetailField>
        <DetailField label="Emergency contact">
          {shown(emergencyContact)}
        </DetailField>
        <div className="flex flex-col gap-4">
          <DetailField label="Raised by">
            {escalation.started_by?.name ?? "—"}
          </DetailField>
          <DetailField label="Closed by">
            {escalation.closed_by?.name ?? "—"}
          </DetailField>
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-4">
        <DetailField label="Suicide assessment risk">
          {shown(text("suicideAssessmentRisk"))}
        </DetailField>
        <DetailField label="Reason for escalation">
          {shown(
            text("reasonForEscalation") ??
              text("explainReasonForAlert") ??
              escalation.reason,
          )}
        </DetailField>
        <DetailField label="Information provided by mentor">
          {shown(text("informationProvidedByMentor"))}
        </DetailField>
        <DetailField label="Conversations with emergency contact / first responders">
          {shown(text("conversationsWithEmergencyContact"))}
        </DetailField>
        <DetailField label="Next steps">
          {shown(text("nextSteps") ?? text("nextStepsSafetyPlan"))}
        </DetailField>
        <DetailField label="Safety plan">
          {shown(text("safetyPlan"))}
        </DetailField>
      </div>
    </Panel>
  );
}

function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Field
      label={label}
      labelClassName={DETAIL_LABEL}
      valueClassName={DETAIL_VALUE}
    >
      {children}
    </Field>
  );
}

function FlagDetail({ alert }: { alert: RiskFlag }) {
  const categories = [
    ...new Set(alert.categories?.map((c) => c.category) ?? []),
  ];
  const reasons =
    alert.categories?.map((c) => c.explanation).filter(Boolean) ?? [];
  const content = alert.sourceContent ?? alert.transcribedText;

  return (
    <Panel>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        <DetailField label="Media type">
          <Pill tone="outline">
            {SOURCE_TYPE_LABELS[alert.sourceType] ?? alert.sourceType}
          </Pill>
        </DetailField>
        <DetailField label="Priority">
          <Pill
            className={cn(
              "px-4",
              PRIORITY_PILL[alert.priority] ?? PRIORITY_PILL.STANDARD,
            )}
          >
            {
              (PRIORITY_CONFIG[alert.priority] ?? PRIORITY_CONFIG.STANDARD)
                .label
            }
          </Pill>
        </DetailField>
        <DetailField label="Category">
          <span className="flex flex-wrap gap-1">
            {categories.map((category) => (
              <Pill key={category} tone="outline">
                {CATEGORY_LABELS[category] ?? category}
              </Pill>
            ))}
          </span>
        </DetailField>
        <div className="flex flex-col gap-4">
          <DetailField label="Detected">
            {DateTime.fromISO(alert.created_at).toFormat(
              "LLL d, yyyy · h:mm a",
            )}
          </DetailField>
          <DetailField label="Assigned to">
            {alert.closedByUser?.name ?? "—"}
          </DetailField>
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-4">
        <DetailField label="Flagged content">
          {content ? (
            <blockquote className="border-l-2 border-[#cbc6be] pl-3 italic">
              “{content}”
            </blockquote>
          ) : (
            "—"
          )}
          {alert.mentorshipId ? (
            <Link
              to="/mentorships/$mentorshipId"
              params={{ mentorshipId: alert.mentorshipId }}
              onClick={() =>
                trackEvent(PostHogEvents.CARE_PAGE_VIEW_CHAT_CLICKED, {
                  alert_id: alert.id,
                })
              }
              className="mt-3 inline-flex h-8 items-center justify-center rounded-full border border-[#8a8275] bg-white px-4 font-body text-xs leading-4 text-black"
            >
              View Chat
            </Link>
          ) : null}
        </DetailField>
        <DetailField label="Why this was flagged">
          {reasons.length > 0 ? reasons.join(" ") : "—"}
        </DetailField>
      </div>
    </Panel>
  );
}
